import type { Item } from '../content/types.ts';
import type { Character } from '../domain/character.ts';
import type { CustomItemLibrary } from '../domain/customItem.ts';
import type { CustomItemDraft, CustomItemProblem } from '../rules/customItems.ts';
import {
  applyCustomItemEdit,
  buildCustomItem,
  checkCustomItemDraft,
  exactDraftFromItem,
} from '../rules/customItems.ts';
import type { CustomItemRepository } from '../persistence/customItemRepository.ts';
import type { LibraryStatus } from '../persistence/customContentRepository.ts';
import type { CustomBackgroundRepository } from '../persistence/customBackgroundRepository.ts';
import type { CharacterRepository } from '../persistence/characterRepository.ts';
import { contentRegistry, setCustomContent } from '../content/dccCoreContent.ts';
import { newId } from './ids.ts';
import { checkCharacterIntegrity } from './characterIntegrity.ts';
import type { InventoryCatalogue } from '../rules/inventory.ts';
import {
  collectCustomReferences,
  collectReversibleReferences,
} from '../portable/customDependencies.ts';
import type { IdAllocatorDeps } from './customContentIds.ts';
import { allocateCustomId } from './customContentIds.ts';
import { describeReferences, findContentReferences } from './customContentUsage.ts';

/**
 * Creating, editing and deleting the player's own Items.
 *
 * Deliberately the same shape as `customSkillService` and
 * `customBackgroundService`: check the draft in the rules layer, write the
 * library, install the result in the catalogue. No rules of its own.
 */

export interface CustomItemDeps extends IdAllocatorDeps {
  customItems: CustomItemRepository;
  now: () => string;
}

export type CustomItemOutcome =
  | { ok: true; library: CustomItemLibrary; item: Item }
  | { ok: false; problems: CustomItemProblem[] };

export async function loadCustomItemsIntoCatalogue(deps: {
  customItems: CustomItemRepository;
}): Promise<{ library: CustomItemLibrary; problems: string[]; status: LibraryStatus }> {
  const { library, problems, status } = await deps.customItems.load();
  const rejected = setCustomContent({ items: library.items });

  const all = [...problems];
  for (const { item, reason } of rejected.items) {
    all.push(`Custom Item "${item.name}" was not loaded because ${reason}.`);
  }
  if (status === 'unreadable') {
    all.push(
      'Your custom Item file has been left exactly as it is. Crawlers keep the Items they own; ' +
        'those Items will not be usable until the file can be read.',
    );
  }
  return { library, problems: all, status };
}

export async function createCustomItem(
  library: CustomItemLibrary,
  draft: CustomItemDraft,
  deps: CustomItemDeps,
): Promise<CustomItemOutcome> {
  const problems = checkCustomItemDraft(
    draft,
    contentRegistry.allItems(),
    null,
    (id) => contentRegistry.getAttack(id),
    (id) => contentRegistry.getSkill(id),
  );
  if (problems.length > 0) return { ok: false, problems };

  // RULE DCC-CUSTOM-011 - allocated across every STORED library.
  const allocated = await allocateCustomId(draft.name, deps, newId);
  if (!allocated.ok) return { ok: false, problems: [{ field: 'name', message: allocated.message }] };

  const item = buildCustomItem(draft, allocated.id, deps.now());
  const saved = await deps.customItems.saveItems(library, [...library.items, item]);
  setCustomContent({ items: saved.items });
  return { ok: true, library: saved, item };
}

/** Duplicate a core or stored custom Item without silently dropping mechanics. */
export async function duplicateCustomItem(
  library: CustomItemLibrary,
  sourceItemId: string,
  newName: string,
  deps: CustomItemDeps,
): Promise<CustomItemOutcome> {
  // Stored definitions are authoritative for repairable/rejected custom content.
  const source = library.items.find((item) => item.id === sourceItemId) ?? contentRegistry.getItem(sourceItemId);
  if (!source) {
    return {
      ok: false,
      problems: [{ field: 'id', message: 'That Item is not available to duplicate.' }],
    };
  }

  const converted = exactDraftFromItem(source);
  if (!converted.ok) {
    return {
      ok: false,
      problems: [{ field: 'modifiers', message: converted.message }],
    };
  }

  const draft: CustomItemDraft = { ...converted.draft, name: newName };
  return createCustomItem(library, draft, deps);
}

/**
 * RULE DCC-CUSTOM-013 - a STRUCTURAL edit to an Item already in use is refused
 * when it would leave a crawler with an impossible equipment layout.
 *
 * The catalogue updates the moment an Item is saved, and no CharacterChange
 * occurs, so nothing else would catch it. Two examples that were both possible:
 *
 *  - an equipped Accessory whose slot is changed to Torso, on a crawler already
 *    wearing a Torso item: two Torso items equipped, both bonuses applying;
 *  - an equipped Item whose slot becomes null: still marked `equipped: 1`, but
 *    now unequippable by definition.
 *
 * The structural fields are those capable of invalidating an equipment layout -
 * `slot` and `handUse`. A name, description, kind or consumable flag cannot,
 * and a MODIFIER change is an intentional content edit: changing what a
 * player's own Item does is the point of being able to edit it.
 *
 * Crawler inventory is never adjusted to make the edit fit. Silently unequipping
 * someone's armour to permit a definition change is not a decision this can
 * make for them.
 *
 * Fails closed on a crawler that cannot be read, for structural edits only.
 */
export async function editCustomItem(
  library: CustomItemLibrary,
  itemId: string,
  draft: CustomItemDraft,
  // `characters` is REQUIRED, not optional. An optional dependency means a
  // caller can omit it and bypass DCC-CUSTOM-013 entirely, which would make the
  // service boundary a suggestion rather than an enforcement point. The service
  // still skips the scan internally for edits that cannot affect a layout.
  deps: CustomItemDeps & { characters: CharacterRepository },
): Promise<CustomItemOutcome> {
  const existing = library.items.find((i) => i.id === itemId);
  if (!existing) {
    return { ok: false, problems: [{ field: 'id', message: 'That custom Item no longer exists.' }] };
  }

  const problems = checkCustomItemDraft(
    draft,
    contentRegistry.allItems(),
    itemId,
    (id) => contentRegistry.getAttack(id),
    (id) => contentRegistry.getSkill(id),
  );
  if (problems.length > 0) return { ok: false, problems };

  const updated = applyCustomItemEdit(existing, draft);

  const structural = describeStructuralChange(existing, updated);
  if (structural !== null) {
    const check = await checkEditAgainstCrawlers(updated, deps.characters);
    if (check !== null) return { ok: false, problems: [{ field: structural, message: check }] };
  }

  const saved = await deps.customItems.saveItems(
    library,
    library.items.map((i) => (i.id === itemId ? updated : i)),
  );
  setCustomContent({ items: saved.items });
  return { ok: true, library: saved, item: updated };
}

/** Which structural field changed, or null when nothing that matters did. */
function describeStructuralChange(
  before: Item,
  after: Item,
): 'slot' | 'handUse' | 'weaponAttackId' | null {
  if (before.slot !== after.slot) return 'slot';
  // Only meaningful in Hands/Holding, but a change there moves an equipped item
  // between two separate capacities (RULE DCC-INV-004).
  if ((before.handUse ?? null) !== (after.handUse ?? null)) return 'handUse';
  // RULE DCC-INV-016 - the third structural field, and it belongs here for
  // exactly the reason the other two do: it can invalidate a layout that is
  // legal right now. A custom sword linked to Longsword sits happily beside a
  // shield; re-point it at Warhammer and the crawler is holding three hands
  // worth of gear. Refused rather than silently made invalid.
  if (before.weaponAttackId !== after.weaponAttackId) return 'weaponAttackId';
  return null;
}

/**
 * Runs the ordinary catalogue-aware integrity check against every crawler that
 * refers to the Item, using a catalogue in which the edit has ALREADY been
 * applied. Returns the refusal message, or null when the edit is safe.
 */
/**
 * Whether a structural change to an Item is safe for the crawlers that refer to
 * it. Returns the refusal message, or null.
 *
 * RULE DCC-CUSTOM-013, second half. Which crawlers count is decided by
 * `collectCustomReferences` - the same whole-record walk portability and
 * deletion use - NOT by current inventory.
 *
 * Two DIFFERENT questions are then asked of each of them, in this order:
 *
 *  1. Could reversible history bring this Item back? `collectReversibleReferences`
 *     walks `changeLog[].undo` alone. If so the structural fields are frozen,
 *     whether or not the crawler owns the Item right now.
 *  2. Otherwise, is the CURRENT layout still legal with the edit applied?
 *
 * Asking the second question first was a defect: owning a stowed Item made its
 * present layout legal and silently suppressed the history check, leaving an
 * audited Undo able to restore a two-handed weapon beside a Shield.
 *
 * POLICY, stated plainly because it is narrower than it could be: while a
 * crawler's reversible history still references an Item, its structural fields
 * are FROZEN. Replaying every historical state to work out which are still
 * reachable would be a great deal of machinery for a rare edit, and getting it
 * subtly wrong would reintroduce exactly the hole it was meant to close. The
 * change log is bounded, so the reference ages out with play; and name and
 * modifier edits stay available throughout.
 */
async function checkEditAgainstCrawlers(
  updated: Item,
  characters: CharacterRepository,
): Promise<string | null> {
  const storedIds = await characters.listStoredIds();
  const broken: string[] = [];
  const inHistory: string[] = [];
  const uninspectable: string[] = [];

  // The catalogue as it WOULD be. Nothing is installed: an edit that is going
  // to be refused must not change what the running application sees.
  const proposed: InventoryCatalogue = {
    lookupItem: (id) => (id === updated.id ? updated : contentRegistry.getItem(id)),
    // Attacks are core content and this edit cannot change them, so the live
    // registry is the right answer. It has to be HERE all the same: held
    // occupancy depends on the Attack (RULE DCC-INV-016), and re-pointing an
    // Item at a two-handed weapon is exactly the edit this check exists for.
    lookupAttack: (id) => contentRegistry.getAttack(id),
  };

  for (const characterId of storedIds) {
    let character: Character;
    try {
      character = await characters.load(characterId);
    } catch {
      uninspectable.push(characterId);
      continue;
    }
    // The WHOLE record, not just what is held now.
    if (!collectCustomReferences(character).has(updated.id)) continue;

    // REVERSIBLE HISTORY FIRST, and this order is the fix for a real defect.
    // Asking "is it in the inventory?" first meant that owning the Item
    // SUPPRESSED the history check: a Sword stowed with equipped: 0 looked like
    // an ordinary current reference, its present layout was legal, and the edit
    // passed - leaving an audited Undo able to restore a two-handed weapon
    // beside a Shield. Current ownership and reachable-by-Undo are separate
    // questions, so they are asked separately, and the conservative one wins.
    if (collectReversibleReferences(character).has(updated.id)) {
      inHistory.push(character.name);
      continue;
    }

    if (character.inventory.some((e) => e.itemId === updated.id)) {
      const report = checkCharacterIntegrity(character, proposed);
      if (!report.ok) broken.push(`${character.name} (${report.problems[0] ?? 'invalid'})`);
      continue;
    }
    // Referenced, but neither owned now nor restorable: an ordinary mention
    // elsewhere in the record. Frozen anyway, under the same policy.
  }

  if (uninspectable.length > 0) {
    return (
      'Nothing has been changed. ' +
      `${uninspectable.length} crawler${uninspectable.length === 1 ? '' : 's'} could not be ` +
      'opened, so there is no way to tell whether this change would break them: ' +
      `${uninspectable.join(', ')}.`
    );
  }
  if (broken.length > 0) {
    return (
      `This change would leave ${broken.join(', ')} with equipment they cannot have. ` +
      'Unequip it on those crawlers first. Nothing has been changed.'
    );
  }
  if (inHistory.length > 0) {
    return (
      `${inHistory.join(', ')} can still undo a change that would bring this Item back, so ` +
      'where it is worn, how it is held and which weapon it is cannot change yet. You can ' +
      'still rename it or alter its bonuses. Nothing has been changed.'
    );
  }
  return null;
}

export interface ItemUsage {
  characterId: string;
  characterName: string;
  quantity: number;
}

export interface ItemUsageScan {
  usage: ItemUsage[];
  uninspectable: { characterId: string; reason: string }[];
  /** Custom Backgrounds whose definition grants this Item. */
  requiredBy: { backgroundId: string; backgroundName: string }[];
}

/**
 * Who is holding an Item, and who could not be checked.
 *
 * RULE DCC-CUSTOM-008 - a crawler that will not load may be exactly the one
 * holding it, so it is reported rather than skipped.
 */
export async function findItemUsage(
  itemId: string,
  characters: CharacterRepository,
): Promise<Omit<ItemUsageScan, 'requiredBy'>> {
  const storedIds = await characters.listStoredIds();
  const usage: ItemUsage[] = [];
  const uninspectable: { characterId: string; reason: string }[] = [];

  for (const characterId of storedIds) {
    let character: Character;
    try {
      character = await characters.load(characterId);
    } catch (err) {
      uninspectable.push({
        characterId,
        reason: err instanceof Error ? err.message : String(err),
      });
      continue;
    }
    // The inventory AND the creation record: a crawler granted an Item at
    // creation still refers to it even after spending every copy.
    const held = character.inventory.find((e) => e.itemId === itemId);
    const granted =
      character.origin?.grants.some((g) => g.itemId === itemId) ||
      character.origin?.backgrounds.some((b) => b.grantedItems.some((g) => g.itemId === itemId));
    if (held || granted) {
      usage.push({
        characterId: character.id,
        characterName: character.name,
        quantity: held?.quantity ?? 0,
      });
    }
  }
  return { usage, uninspectable };
}

export type DeleteItemOutcome =
  | { ok: true; library: CustomItemLibrary }
  | { ok: false; scan: ItemUsageScan; message: string };

/**
 * RULE DCC-CUSTOM-005 for Items, failing closed the same way.
 *
 * Three ways to refuse: a crawler holds or was granted it; a custom Background
 * grants it, which would drop that Background out of the catalogue
 * (DCC-CUSTOM-009) and so quietly take a second piece of content with it; or a
 * crawler could not be read, so its use cannot be ruled out.
 */
export async function deleteCustomItem(
  library: CustomItemLibrary,
  itemId: string,
  deps: CustomItemDeps & {
    characters: CharacterRepository;
    customBackgrounds: CustomBackgroundRepository;
  },
): Promise<DeleteItemOutcome> {
  // The WHOLE record (RULE DCC-CUSTOM-008), so an Item that was removed from a
  // crawler but is still restorable by Undo counts as in use.
  const { references, uninspectable } = await findContentReferences(itemId, deps.characters);
  const usage: ItemUsage[] = references.map((r) => ({
    characterId: r.characterId,
    characterName: r.characterName,
    quantity: r.character.inventory.find((e) => e.itemId === itemId)?.quantity ?? 0,
  }));

  const backgroundLibrary = await deps.customBackgrounds.load();
  if (backgroundLibrary.status === 'unreadable') {
    return {
      ok: false,
      scan: { usage, uninspectable, requiredBy: [] },
      message:
        'Nothing has been deleted. Your custom Background file could not be read, so there is ' +
        'no way to tell whether a Background grants this Item.',
    };
  }

  const requiredBy = backgroundLibrary.library.backgrounds
    .filter((b) => (b.startingEquipment ?? []).some((g) => g.itemId === itemId))
    .map((b) => ({ backgroundId: b.id, backgroundName: b.name }));

  const scan: ItemUsageScan = { usage, uninspectable, requiredBy };

  if (uninspectable.length > 0) {
    return {
      ok: false,
      scan,
      message:
        `Nothing has been deleted. ${uninspectable.length} crawler` +
        `${uninspectable.length === 1 ? '' : 's'} could not be opened, so there is no way to ` +
        `tell whether they have this Item: ${uninspectable.map((u) => u.characterId).join(', ')}.`,
    };
  }

  if (requiredBy.length > 0) {
    return {
      ok: false,
      scan,
      message:
        `Your Background${requiredBy.length === 1 ? '' : 's'} ` +
        `${requiredBy.map((b) => b.backgroundName).join(', ')} grant this Item. Deleting it ` +
        `would stop ${requiredBy.length === 1 ? 'that Background' : 'those Backgrounds'} being ` +
        'offered when you make a crawler. Nothing has been changed.',
    };
  }

  if (usage.length > 0) {
    return {
      ok: false,
      scan,
      message: describeReferences('this Item', references),
    };
  }

  const saved = await deps.customItems.saveItems(
    library,
    library.items.filter((i) => i.id !== itemId),
  );
  setCustomContent({ items: saved.items });
  return { ok: true, library: saved };
}
